import BasicField from '@payaca/components/basicField/BasicField';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import TextareaField from '@payaca/components/textareaField/TextareaField';
import TextareaFieldMentionFormatter from '@payaca/components/textareaField/TextareaFieldMentionFormatter';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { getLengthFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import { PublicEntityTemplate } from '@payaca/types/entity-templates';
import {
  FieldValidationResult,
  FieldValidator,
} from '@payaca/types/fieldValidationTypes';
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import ChecklistItemsControl from '../checklistItemsControl/ChecklistItemsControl';
import './CreateEditTaskTemplateControl.sass';

const taskNameLengthFieldValidator = getLengthFieldValidator({ max: 250 });
const checklistItemLengthNameFieldValidator = getLengthFieldValidator({
  max: 500,
});

type Props = {
  taskTemplate?: Omit<PublicEntityTemplate<'task'>, 'publicId'>;
  onRequestSave?: (
    taskTemplate: Omit<PublicEntityTemplate<'task'>, 'publicId'>
  ) => void;
  allowModifyTemplateNameDescription?: boolean;
};
const CreateEditTaskTemplateControl: FunctionComponent<Props> = ({
  taskTemplate,
  onRequestSave,
  allowModifyTemplateNameDescription = false,
}: Props): JSX.Element => {
  const [checklistItemsCount, setChecklistItemsCount] = useState(0);

  const [showAddTemplateNameDescription, setShowAddTemplateNameDescription] =
    useState<boolean>(
      allowModifyTemplateNameDescription &&
        (!!taskTemplate?.name?.length || !!taskTemplate?.description?.length)
    );

  const initialFormState = useMemo(() => {
    return {
      name: taskTemplate?.name,
      description: taskTemplate?.description,
      entityType: taskTemplate?.entityType || 'task',
      taskType: 'checklist',
      data: {
        ...taskTemplate?.data,
        checklistItems: taskTemplate?.data?.checklistItems
          ? taskTemplate?.data?.checklistItems.map((x) => ({ name: x }))
          : [],
      },
    };
  }, [taskTemplate]);

  const fieldValidators = useMemo(() => {
    const fv: { [fieldName: string]: FieldValidator[] } = {
      name: [taskNameLengthFieldValidator],
    };

    for (let i = 0; i < checklistItemsCount; i++) {
      fv[`checklistItems[${i}].name`] = [checklistItemLengthNameFieldValidator];
    }

    return fv;
  }, [checklistItemsCount]);

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      setChecklistItemsCount(formState.data.checklistItems?.length || 0);

      return (
        <>
          {!showAddTemplateNameDescription &&
            allowModifyTemplateNameDescription && (
              <Button
                className="add-template-name-description-button"
                styleVariant={ButtonStyleVariant.ANCHOR}
                onClick={() => setShowAddTemplateNameDescription(true)}
              >
                Add Template name and description
              </Button>
            )}
          {showAddTemplateNameDescription && (
            <>
              <ValidatedFieldWrapper
                validationResult={validationState['name']}
                isTouched={touchedState['name'] || false}
              >
                <BasicField
                  name="name"
                  value={formState.name || ''}
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                  label="Template name"
                  styleVariant={InputStyleVariant.OUTSIZE}
                />
              </ValidatedFieldWrapper>
              <ValidatedFieldWrapper
                validationResult={validationState['description']}
                isTouched={touchedState['description'] || false}
              >
                <TextareaField
                  name="description"
                  value={formState.description || ''}
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                  label="Template description"
                  styleVariant={InputStyleVariant.OUTSIZE}
                />
              </ValidatedFieldWrapper>
            </>
          )}
          {formState.taskType === 'checklist' && (
            <>
              <ValidatedFieldWrapper
                validationResult={validationState['data.name']}
                isTouched={touchedState['data.name'] || false}
              >
                <BasicField
                  name="data.name"
                  value={formState.data?.name}
                  label="Task name"
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                  styleVariant={InputStyleVariant.OUTSIZE}
                />
              </ValidatedFieldWrapper>
              <div>
                <div className="checklist-items-label">
                  <small>
                    <FieldLabel label={'Checklist'} />
                  </small>
                </div>
                <ChecklistItemsControl
                  checklistItems={formState.data?.checklistItems}
                  fieldNamePrefix={'data.checklistItems'}
                  onChange={onFieldChange}
                  onTouch={onFieldTouch}
                  touchedState={touchedState}
                  validationState={validationState}
                />
              </div>
            </>
          )}
          <TextareaFieldMentionFormatter
            label="Task notes"
            name="data.notes"
            onChange={onFieldChange}
            value={formState.data?.notes}
          />
          <div className="actions-container">
            <Button
              styleVariant={ButtonStyleVariant.OUTSIZE}
              isDisabled={!isValid}
              onClick={() => {
                const { taskType, ...taskTemplate } = formState;
                const { checklistItems, ...data } = taskTemplate.data;

                onRequestSave?.({
                  ...taskTemplate,
                  data: {
                    ...data,
                    checklistItems: checklistItems?.map((x: any) => x.name),
                  },
                } as Omit<PublicEntityTemplate<'task'>, 'publicId'>);
              }}
            >
              {taskTemplate ? 'Save' : 'Create'} Task Template
            </Button>
          </div>
        </>
      );
    },
    [
      taskTemplate,
      onRequestSave,
      showAddTemplateNameDescription,
      allowModifyTemplateNameDescription,
    ]
  );

  return (
    <div className="create-edit-task-template-control">
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
        fieldValidators={fieldValidators}
      />
    </div>
  );
};

export default CreateEditTaskTemplateControl;
